import { useEffect, useState, useCallback } from 'react';
import { Alert, Tooltip, Button } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { usePost, useAuth } from 'hooks';
import { Input, Select, Detail, Loader, Radio } from 'components/common';
import { userAlerts } from 'services/api';
import { getAlertToCreate } from 'selectors/alerts';
import { setAlertToCreate } from 'actions/alerts';
import {
  frequencyEnums,
  frequencyOptions,
  frequencyRadioOptions,
  frequencyRadioOptionsLimited,
} from 'models/frequency';
import { UpgradeLicense } from 'utils/constants';
import Share from './Share';
import { getListsFilters } from './helpers';

const CreateSearch = ({
  sanityCheckLoading,
  sanityError,
  recentItemsResults,
  allUsersLoading,
  onSuccess,
  userOptions,
  checkUserPermissions,
}) => {
  const dispatch = useDispatch();
  const activeAlert = useSelector(getAlertToCreate);
  const [frequency, setFrequency] = useState(null);
  const [showWarning, setShowWarning] = useState('');
  const [sharedUsers, setSharedUsers] = useState([]);
  const { user } = useAuth();

  const [{ res: success, loading: creating, error }, createAlert] = usePost({
    url: userAlerts,
  });

  useEffect(() => {
    if (success && frequency !== null) {
      onSuccess(activeAlert);
      dispatch(setAlertToCreate());
      setFrequency(null);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success, onSuccess, dispatch, frequency, activeAlert]);

  const handleCreateChange = useCallback(
    key => value => {
      const updatedAlert = {
        ...activeAlert,
        [key]: value,
      };

      dispatch(setAlertToCreate(updatedAlert));
    },
    [activeAlert, dispatch]
  );

  const handleChildAlertChange = useCallback(
    key => value => {
      const updatedAlert = {
        ...activeAlert,
        childAlert: {
          ...activeAlert.childAlert,
          [key]: value,
        },
      };

      dispatch(setAlertToCreate(updatedAlert));
    },
    [activeAlert, dispatch]
  );

  const handleUsersChange = useCallback(
    v => {
      handleChildAlertChange('users')(v);
      setSharedUsers(v);

      const warning = checkUserPermissions(v);
      setShowWarning(warning);
    },
    [handleChildAlertChange, checkUserPermissions]
  );

  const handleCreate = () => {
    const frequency =
      activeAlert.frequencyRadio === 'Alert'
        ? frequencyOptions.find(freq => freq.value === activeAlert.frequency)
            .enumValue
        : frequencyEnums.NoAlert;
    setFrequency(frequency);
    let childAlert = {
      users: [],
      frequency: frequencyEnums.NoAlert,
    };
    if (activeAlert.childAlert.users.length) {
      const childFrequency =
        activeAlert.childAlert.frequencyRadio === 'Alert'
          ? frequencyOptions.find(
              freq => freq.value === activeAlert.childAlert.frequency
            ).enumValue
          : frequencyEnums.NoAlert;
      childAlert = {
        ...activeAlert.childAlert,
        frequency: childFrequency,
      };
    }
    createAlert({
      ...activeAlert,
      name: activeAlert.name?.trim(),
      ...getListsFilters(activeAlert),
      frequency,
      recentItemsResults: recentItemsResults,
      childAlert,
    });
  };

  if (!activeAlert) return null;

  const isValid =
    activeAlert.name?.trim() &&
    activeAlert.monthlyKeywords &&
    activeAlert.frequency;

  return (
    <>
      {sanityCheckLoading || allUsersLoading ? (
        <Loader />
      ) : sanityError ? (
        <div />
      ) : (
        <div>
          <Detail
            label={
              <div>
                Search Name <span style={{ color: 'red' }}>*</span>
              </div>
            }
          >
            <Input
              showCount
              disabled={creating}
              maxLength={50}
              value={activeAlert.name}
              onChange={handleCreateChange('name')}
              placeholder={
                user.disableExport
                  ? 'Type the name of your saved search'
                  : 'Type the name of your saved search or alert'
              }
            />
          </Detail>
          {!user.disableExport && (
            <Detail style={{ marginTop: '21px', marginBottom: '21px' }}>
              <Radio
                radioStyle={{ height: '30px', lineHeight: '30px' }}
                value={activeAlert.frequencyRadio}
                onChange={handleCreateChange('frequencyRadio')}
                options={
                  user.isPartnerAccount
                    ? frequencyRadioOptionsLimited
                    : frequencyRadioOptions
                }
              />
              <Tooltip title={user.isPartnerAccount ? <UpgradeLicense /> : ''}>
                <Select
                  disabled={creating || activeAlert.frequencyRadio !== 'Alert'}
                  value={activeAlert.frequency}
                  onChange={handleCreateChange('frequency')}
                  options={frequencyOptions}
                  style={{ minWidth: '95px' }}
                  disableTooltip
                />
              </Tooltip>
            </Detail>
          )}
          <Share
            userOptions={userOptions}
            activeAlert={activeAlert}
            sharedUsers={sharedUsers}
            handleUsersChange={handleUsersChange}
            showWarning={showWarning}
            loading={creating}
            handleChildAlertChange={handleChildAlertChange}
          />
        </div>
      )}

      {sanityError && (
        <Alert
          message={
            'This is not a valid search, please correct search before saving again.'
          }
          type='error'
        />
      )}
      {error && (
        <Alert
          message={
            error.message?.message ??
            'Something went wrong. If the issue persists contact your Customer Success Manager.'
          }
          type='error'
        />
      )}
      <div
        style={{
          marginTop: '20px',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '8px',
        }}
      >
        <Button
          onClick={() => dispatch(setAlertToCreate())}
          disabled={creating}
          data-testid='create-cancel-button'
        >
          Cancel
        </Button>
        <Button
          type='primary'
          onClick={handleCreate}
          disabled={creating || !isValid}
          hidden={sanityCheckLoading || sanityError}
          data-testid='create-save-button'
        >
          Save
        </Button>
      </div>
    </>
  );
};

export default CreateSearch;
